import epiSchema from '../data/epi-schema.json';
import { TAB_CONFIGS } from '../types/constants';
import { emptyText, text } from './epiHelpers';
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '../../../translate/types';
import type {
  EpiComposition,
  EpiCompositionReport,
  EpiDocumentType,
  EpiIdmpLink,
  EpiIdmpRef,
  EpiInspectResult,
  EpiPredefinedEntry,
  EpiText,
} from '../types/types';

/** Which products the user assigned, keyed by `${type}:${order}`. */
export type ImportAssignment = Record<string, string[]>;

export const rowKey = (type: EpiDocumentType, order: number): string =>
  `${type}:${order}`;

/**
 * The products a row starts with. A composition that is the only one of its
 * type covers everything — that is what the document is saying — so it is
 * pre-selected. Where a type repeats, the designations are what tells the
 * compositions apart and only the user can answer.
 */
export function defaultAssignment(
  reports: EpiCompositionReport[],
  productIds: string[]
): ImportAssignment {
  const perType = reports.reduce<Record<string, number>>((acc, r) => {
    acc[r.type] = (acc[r.type] ?? 0) + 1;
    return acc;
  }, {});

  return reports.reduce<ImportAssignment>((acc, r) => {
    acc[rowKey(r.type, r.order)] = perType[r.type] === 1 ? productIds : [];
    return acc;
  }, {});
}

/**
 * Why the assignment cannot be saved yet, or null. Per document type the
 * compositions **partition** the ePI's products: each covers at least one, no
 * product is covered twice, and none is left out.
 */
export function assignmentProblem(
  reports: EpiCompositionReport[],
  assignment: ImportAssignment,
  productIds: string[]
): string | null {
  const byType = reports.reduce<Record<string, EpiCompositionReport[]>>(
    (acc, r) => {
      (acc[r.type] ??= []).push(r);
      return acc;
    },
    {}
  );

  for (const [type, rows] of Object.entries(byType)) {
    const seen = new Set<string>();
    for (const row of rows) {
      const chosen = assignment[rowKey(row.type, row.order)] ?? [];
      if (chosen.length === 0) {
        return `"${row.title}" covers no product. Every document has to cover at least one.`;
      }
      for (const id of chosen) {
        if (seen.has(id)) {
          return `Two ${type} documents cover the same product. Each product belongs to exactly one.`;
        }
        seen.add(id);
      }
    }
    const missing = productIds.filter(id => !seen.has(id));
    if (missing.length > 0) {
      return `${missing.length} product${missing.length === 1 ? '' : 's'} not covered by any ${type} document. Every product needs one.`;
    }
  }
  return null;
}

const isText = (value: object): boolean => {
  const keys = Object.keys(value);
  return (
    keys.length > 0 &&
    keys.every(k => (SUPPORTED_LANGUAGES as string[]).includes(k))
  );
};

/**
 * `inspect` fills only the document's language, but the model requires `sr`,
 * so an English import would save `{ en }` wherever the merge has nothing to
 * carry across. Every text starts with both keys instead, and the merge only
 * ever overwrites one.
 */
const withBothLanguages = <T>(node: T): T => {
  if (Array.isArray(node)) return node.map(withBothLanguages) as T;
  if (!node || typeof node !== 'object') return node;
  if (isText(node)) return { ...emptyText(), ...node };
  return Object.fromEntries(
    Object.entries(node).map(([key, value]) => [key, withBothLanguages(value)])
  ) as T;
};

/** The compositions a document carries, per tab, in report order. */
export function compositionsByTab(
  result: EpiInspectResult
): Record<string, EpiComposition[]> {
  const tabs: Record<string, EpiComposition[]> = {};
  for (const report of result.compositions) {
    const incoming = result.instance[report.type]?.[report.order];
    if (incoming) (tabs[report.type] ??= []).push(withBothLanguages(incoming));
  }
  return tabs;
}

/**
 * Writes the assigned products onto each composition. `inspect` returns them
 * with none: the document has never heard of a product, only of the
 * designations it prints.
 */
export function assignProducts(
  byTab: Record<string, EpiComposition[]>,
  reports: EpiCompositionReport[],
  assignment: ImportAssignment,
  productsById: Record<string, EpiIdmpRef>
): Record<string, EpiComposition[]> {
  const chosenFor = (report: EpiCompositionReport): EpiIdmpLink[] =>
    (assignment[rowKey(report.type, report.order)] ?? [])
      .map(id => productsById[id])
      .filter(Boolean)
      .map(({ id }) => ({ id }));

  const tabs: Record<string, EpiComposition[]> = {};
  for (const report of reports) {
    const incoming = byTab[report.type]?.[report.order];
    if (incoming) {
      (tabs[report.type] ??= []).push({
        ...incoming,
        medicinalProducts: chosenFor(report),
      });
    }
  }
  return tabs;
}

/**
 * Every string the schema seeds, in either language. A created ePI is already
 * full of them — 121 English fields before anybody has typed a word — so a
 * count that includes them says nothing about what an import would cost.
 */
/**
 * The QRD default title and content per RMS term code. Keyed by code rather
 * than by path because that is what identifies a section globally — catalogue
 * keys repeat across parents, codes do not — so a section can be given its
 * template text without knowing where in the tree it sits.
 */
const defaultsByRmsCode = (() => {
  const byCode: Record<string, { title?: EpiText; content?: EpiText }> = {};
  const walk = (node: unknown): void => {
    if (Array.isArray(node)) return node.forEach(walk);
    if (!node || typeof node !== 'object') return;
    const record = node as any;
    const code = record.properties?.rmsCode?.default;
    if (typeof code === 'string' && code) {
      byCode[code] = {
        title: record.properties?.title?.default,
        content: record.properties?.content?.default,
      };
    }
    Object.values(record).forEach(walk);
  };
  walk(epiSchema);
  return byCode;
})();

const seededText = (() => {
  const values = new Set<string>();
  const add = (text: object): void =>
    Object.values(text).forEach(v => {
      if (typeof v === 'string' && v.trim()) values.add(v.trim());
    });
  // A predefined title is the list's, written in both languages by whoever
  // adds the section — the template talking, like a default.
  const addPredefined = (entries: EpiPredefinedEntry[]): void =>
    entries.forEach(entry => {
      add(entry.title);
      addPredefined(entry.children ?? []);
    });
  const walk = (node: unknown): void => {
    if (Array.isArray(node)) return node.forEach(walk);
    if (!node || typeof node !== 'object') return;
    const record = node as Record<string, unknown>;
    if (record.type === 'object' && record.default) {
      add(record.default as Record<string, unknown>);
    }
    if (Array.isArray(record['x-predefined'])) {
      addPredefined(record['x-predefined'] as EpiPredefinedEntry[]);
    }
    Object.values(record).forEach(walk);
  };
  walk(epiSchema);
  return values;
})();

/**
 * How many fields the ePI holds in `lang` that **somebody wrote** — anything
 * still matching a schema default is the template talking, not an author.
 * Import fills one language and replaces the documents whole, so a Serbian
 * file imported over authored English discards it; this is what says so
 * before it happens rather than after.
 *
 * Matching by value rather than by path: a custom section and a packaging
 * block have no schema path to compare against, and text identical to a QRD
 * default is that default wherever it sits.
 */
export function countTextIn(
  compositions: EpiComposition[],
  lang: SupportedLanguage
): number {
  const filled = (value: EpiText | undefined): number =>
    authored(value, lang) ? 1 : 0;

  const inSections = (sections: unknown): number => {
    if (Array.isArray(sections)) {
      return sections.reduce<number>((n, item) => n + inSection(item), 0);
    }
    if (!sections || typeof sections !== 'object') return 0;
    return Object.values(sections).reduce<number>(
      (n, item) => n + inSection(item),
      0
    );
  };

  const inSection = (section: any): number => {
    if (!section || typeof section !== 'object') return 0;
    // A repeatable key holds an array of blocks, not a section.
    if (Array.isArray(section)) return inSections(section);
    if (section.toBeDeleted) return 0;
    return (
      filled(section.title) +
      filled(section.content) +
      inSections(section.sections) +
      inSections(section.customSections)
    );
  };

  return compositions
    .filter(c => !c.toBeDeleted)
    .reduce(
      (n, c) =>
        n + filled(c.rootContent) + filled(c.title) + inSections(c.sections),
      0
    );
}

/** True when this field holds text somebody wrote, rather than a QRD default. */
const authored = (
  value: EpiText | undefined,
  lang: SupportedLanguage
): boolean => {
  const written = text(value, lang).trim();
  return !!written && !seededText.has(written);
};

/** What the merge managed to carry across, and what it could not. */
export interface MergeOutcome {
  compositions: Record<string, EpiComposition[]>;
  /** Authored fields in the other language that found a home. */
  kept: number;
  /** Authored fields whose section the imported document does not have. */
  dropped: number;
  /**
   * The sections those fields belong to, named in the language being lost —
   * one entry per section however many of its fields go with it. A count
   * alone does not tell an author whether it matters.
   */
  lost: string[];
  /**
   * Custom sections the document brings that are not imported, named in the
   * document's language: the ePI's own under the same parent hold only the
   * other language, so they keep the structure.
   */
  skipped: string[];
}

/** How many imported headings matched no predefined section, in every document. */
export function countUnmatched(result: EpiInspectResult): number {
  const inSections = (sections: unknown): number =>
    Object.values(sections ?? {})
      .flatMap(value => (Array.isArray(value) ? value : [value]))
      .reduce<number>((n, section) => n + inSection(section), 0);
  const inCustom = (custom: any[] = []): number =>
    custom.reduce<number>(
      (n, c) => n + (c.unmatched ? 1 : 0) + inCustom(c.customSections),
      0
    );
  const inSection = (section: any): number =>
    section
      ? inSections(section.sections) + inCustom(section.customSections)
      : 0;

  return result.compositions.reduce(
    (n, report) =>
      n + inSections(result.instance[report.type]?.[report.order]?.sections),
    0
  );
}

/**
 * Carries the language the document does not fill across into it.
 *
 * A document is written in one language, so importing replaces one side of
 * every field and would take the other with it. What saves the other side is
 * that a declared section is addressed by its schema key — identical in both
 * languages by construction — so the two trees can be walked together.
 *
 * Custom sections and repeatable blocks have no such address: a custom
 * section's identity is a uuid the author invented, and a block's is its
 * `natureType`, which is itself language-specific text. Position is all that
 * is left, and it is only trustworthy when the two structures agree on how
 * many there are — one inserted at the top shifts every pairing after it, and
 * text attached to the wrong section is worse than text that is missing. So
 * they are paired only where the counts match, and counted as dropped where
 * they do not.
 */
export function mergeOtherLanguage(
  byTab: Record<string, EpiComposition[]>,
  existingByTab: Record<string, EpiComposition[]>,
  other: SupportedLanguage
): MergeOutcome {
  const imported = SUPPORTED_LANGUAGES.find(l => l !== other) ?? other;
  let kept = 0;
  let dropped = 0;
  const lost: string[] = [];
  const skipped: string[] = [];

  /**
   * Carries **any** text the other language holds, not only what somebody
   * wrote: a QRD default is that language's version of the section, and
   * dropping it leaves the other language blank where it used to read as a
   * template. Only authored text is counted, because only that is worth
   * reporting.
   */
  const carry = (
    incoming: EpiText | undefined,
    existing: EpiText | undefined,
    fallback?: EpiText
  ): EpiText | undefined => {
    const existingText = text(existing, other).trim();
    const written = existingText || text(fallback, other).trim();
    if (!written) return incoming;
    if (existingText && authored(existing, other)) kept += 1;
    return { ...(incoming ?? {}), [other]: written } as EpiText;
  };

  // Which document the walk is in, for naming what it loses. A closure rather
  // than a parameter because every merge below runs inside one tab's turn of
  // the loop, synchronously.
  let where = '';

  /** Visits every section under here holding authored text in `lang`. */
  const eachAuthored = (
    node: unknown,
    lang: SupportedLanguage,
    visit: (name: string, fields: number) => void
  ): void => {
    if (Array.isArray(node)) {
      return node.forEach(n => eachAuthored(n, lang, visit));
    }
    if (!node || typeof node !== 'object') return;
    const section = node as any;
    if (section.toBeDeleted) return;
    const fields =
      (authored(section.title, lang) ? 1 : 0) +
      (authored(section.content, lang) ? 1 : 0) +
      (authored(section.natureType, lang) ? 1 : 0);
    if (fields > 0) {
      const name = text(section.title, lang).trim() || 'Untitled';
      visit(where ? `${where} · ${name}` : name, fields);
    }
    Object.values(section.sections ?? {}).forEach(n =>
      eachAuthored(n, lang, visit)
    );
    (section.customSections ?? []).forEach((n: unknown) =>
      eachAuthored(n, lang, visit)
    );
  };

  const holds = (node: unknown, lang: SupportedLanguage): boolean => {
    let found = false;
    eachAuthored(node, lang, () => (found = true));
    return found;
  };

  /** Everything authored under here has nowhere to go. */
  const lose = (node: unknown): void =>
    eachAuthored(node, other, (name, fields) => {
      dropped += fields;
      lost.push(name);
    });

  /**
   * Paired by print `order` — array position is not print order once the BE
   * has merged by id — and only when both sides agree how many there are.
   */
  const mergeList = (incoming: any[], existing: any[] | undefined): any[] => {
    const live = (existing ?? []).filter(item => !item.toBeDeleted);
    if (!live.length) return incoming;
    if (live.length !== incoming.length) {
      live.forEach(lose);
      return incoming;
    }
    // Print position: a custom section's anchor first, then `order`. Blocks
    // carry no anchor, so they compare by `order` alone.
    const anchor = (item: any) => item.afterSequenceOrder ?? Infinity;
    const byPosition = (a: any, b: any) =>
      anchor(a) - anchor(b) || (a.order ?? 0) - (b.order ?? 0);
    const counterparts = [...live].sort(byPosition);
    return [...incoming]
      .sort(byPosition)
      .map((item, i) => mergeSection(item, counterparts[i]));
  };

  /**
   * A predefined section is the same section in both languages by its key, so
   * it pairs by key wherever it sits; one the document no longer has is lost.
   * Position is left to the free and unmatched ones.
   */
  const mergeCustomSections = (incoming: any[], existing: any[]): any[] => {
    const live = existing.filter(item => !item.toBeDeleted);
    const byKey = new Map(
      live.filter(item => item.predefinedKey).map(i => [i.predefinedKey, i])
    );
    const keyed = incoming
      .filter(item => item.predefinedKey)
      .map(item => {
        const counterpart = byKey.get(item.predefinedKey);
        byKey.delete(item.predefinedKey);
        return counterpart ? mergeSection(item, counterpart) : item;
      });
    byKey.forEach(lose);
    return [
      ...keyed,
      ...mergeFreeSections(
        incoming.filter(item => !item.predefinedKey),
        live.filter(item => !item.predefinedKey)
      ),
    ];
  };

  /**
   * Where the counts differ, the language being imported overwrites what it
   * already had — unless the ePI's custom sections there hold only the other
   * language. Then they are that language's structure, the one the document
   * failed to match, so they stay and the document's are not imported.
   */
  const mergeFreeSections = (incoming: any[], live: any[]): any[] => {
    const otherOnly = holds(live, other) && !holds(live, imported);
    if (live.length === incoming.length || !otherOnly) {
      return mergeList(incoming, live);
    }
    eachAuthored(incoming, imported, name => skipped.push(name));
    return live;
  };

  const mergeSection = (incoming: any, existing: any): any => {
    // No counterpart, so nothing to carry — but the section still has a QRD
    // default in the other language, and leaving it empty is not the template
    // an author expects to find under the language they have not written yet.
    const template = defaultsByRmsCode[incoming?.rmsCode] ?? {};
    const merged: any = {
      ...incoming,
      title: carry(incoming.title, existing?.title, template.title),
      content: carry(incoming.content, existing?.content, template.content),
    };
    if (!existing || typeof existing !== 'object') {
      if (incoming?.sections) {
        merged.sections = mergeSections(incoming.sections, {});
      }
      return merged;
    }
    if (incoming.natureType || existing.natureType) {
      merged.natureType = carry(incoming.natureType, existing.natureType);
    }
    if (incoming.sections || existing.sections) {
      merged.sections = mergeSections(
        incoming.sections ?? {},
        existing.sections ?? {}
      );
    }
    if (incoming.customSections || existing.customSections) {
      merged.customSections = mergeCustomSections(
        incoming.customSections ?? [],
        existing.customSections ?? []
      );
    }
    return merged;
  };

  const mergeSections = (
    incoming: Record<string, any>,
    existing: Record<string, any>
  ): Record<string, any> => {
    const merged: Record<string, any> = {};
    for (const [key, value] of Object.entries(incoming)) {
      const counterpart = existing[key];
      merged[key] = Array.isArray(value)
        ? mergeList(value, Array.isArray(counterpart) ? counterpart : undefined)
        : mergeSection(value, counterpart);
    }
    // A section the old ePI had and this document does not.
    for (const [key, value] of Object.entries(existing)) {
      if (!(key in incoming)) lose(value);
    }
    return merged;
  };

  const compositions: Record<string, EpiComposition[]> = {};
  for (const [tab, incoming] of Object.entries(byTab)) {
    where = TAB_CONFIGS.find(t => t.key === tab)?.label ?? tab;
    const existing = (existingByTab[tab] ?? []).filter(c => !c.toBeDeleted);
    compositions[tab] = incoming.map((composition, i) => {
      const counterpart = existing[i];
      if (!counterpart) return composition;
      return {
        ...composition,
        title: carry(composition.title, counterpart.title) as EpiText,
        rootTitle: carry(
          composition.rootTitle,
          counterpart.rootTitle
        ) as EpiText,
        rootContent: carry(composition.rootContent, counterpart.rootContent),
        sections: mergeSections(
          composition.sections ?? {},
          counterpart.sections ?? {}
        ),
      };
    });
    // Documents the ePI had that this file does not replace one-for-one.
    existing.slice(incoming.length).forEach(composition => {
      const fields =
        (authored(composition.title, other) ? 1 : 0) +
        (authored(composition.rootTitle, other) ? 1 : 0) +
        (authored(composition.rootContent, other) ? 1 : 0);
      if (fields > 0) {
        dropped += fields;
        lost.push(
          `${where} · ${text(composition.title, other).trim() || where}`
        );
      }
      Object.values(composition.sections ?? {}).forEach(lose);
    });
  }

  return { compositions, kept, dropped, lost, skipped };
}
